import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Autocomplete, TextField } from "@mui/material";

import { checkForUnexpectedProps } from "services/utility/misc";

import { useDynamicOptions } from "hooks/useDynamicOptions";

/**
 * @name    DynamicAutocompleteField
 *
 * @summary An autocomplete that optionally fetches options dynamically as the
 *          user types.  When optionsCount exceeds DYNAMIC_FETCH_THRESHOLD and
 *          a fetchFn is supplied, typing triggers an API call after a debounce
 *          period and replaces the visible option list with the results.  On
 *          blur the list resets to the original options.
 *
 *          When fetchFn or optionsCount are omitted (or options.length >=
 *          optionsCount) the component behaves identically to a standard
 *          Autocomplete.
 *
 *          Pass a custom renderInput to control appearance (e.g. line-item
 *          styles, Tooltip wrapping).  Omit it for the standard form variant.
 *
 * @prop    options        {array|null}   initial option list
 * @prop    optionsCount   {number|null}  total count of records in the
 *                                        database; enables dynamic fetch when
 *                                        optionsCount > DYNAMIC_FETCH_THRESHOLD
 * @prop    fetchFn        {function}     async (query: string) => option[]
 *                                        called on debounced input change
 * @prop    renderInput    {function}     custom renderInput for the Autocomplete;
 *                                        defaults to a plain outlined TextField
 */
export function DynamicAutocompleteField(props) {
  const {
    name,
    options: externalOptions,
    optionsCount,
    fetchFn,
    optionId = "id",
    optionDisplayText = "name",
    onValueChange,
    disableClearable,
    disabled,
    value,
    label,
    error,
    dataTesting,
    lineReadOnly = false,
    fullWidth,
    maxWidth,
    PopperComponent,
    renderInput: externalRenderInput,
    ...unexpected
  } = props;
  checkForUnexpectedProps("DynamicAutocompleteField", unexpected);

  const {
    options: rawOptions,
    fetchOnChange,
    handleInputChange,
    handleBlur,
  } = useDynamicOptions(externalOptions, optionsCount, fetchFn);

  // ensure the selected value is always present in the options list, even when
  // dynamic fetch returns a filtered subset that doesn't include it
  let options = rawOptions ? [...rawOptions] : rawOptions;
  if (options && value) {
    const found = options.find((o) => o[optionId] === value[optionId]);
    if (!found) {
      options.unshift(value);
    }
  }

  function onChange(_, newValue, reason) {
    if (reason === "clear") {
      onValueChange(name, null);
      return;
    }
    onValueChange(name, newValue);
  }

  function getOptionLabel(option) {
    if (option[optionDisplayText]) {
      return option[optionDisplayText];
    }
    if (!options) {
      return "";
    }
    const matchInOptions = options.find(({ id }) => id === option[optionId]);
    return matchInOptions ? matchInOptions[optionDisplayText] : "";
  }

  function isOptionEqualToValue(option, value) {
    if (!option || !value) {
      return false;
    }
    return (
      value[optionId] === option[optionId] ||
      (value === "" && option[optionId] === "")
    );
  }

  function renderOption(props, option) {
    return (
      <li {...props} data-testing="selectOption" key={option[optionId]}>
        <div style={{ minHeight: "1.5em" }}>{option[optionDisplayText]}</div>
      </li>
    );
  }

  function defaultRenderInput(params) {
    return (
      <TextField
        {...params}
        error={error}
        variant="outlined"
        label={label}
        margin="dense"
        autoComplete="off"
        InputLabelProps={{ shrink: true }}
        InputProps={{ ...params.InputProps }}
        sx={{
          textAlign: "left",
          maxWidth: maxWidth ?? "inherit",
        }}
      />
    );
  }

  if (lineReadOnly) {
    const displayValue = value ? value[optionDisplayText] : "";
    return (
      <div style={{ fontSize: "0.8125rem", padding: "8px 14px" }}>
        {displayValue}
      </div>
    );
  }

  return (
    <Autocomplete
      fullWidth={fullWidth}
      name={name}
      disabled={disabled}
      value={value}
      disableClearable={disableClearable}
      loading={!Array.isArray(options)}
      options={options ? options : []}
      onChange={onChange}
      onInputChange={
        fetchOnChange
          ? (_, value, reason) => handleInputChange(value, reason)
          : undefined
      }
      onBlur={fetchOnChange ? handleBlur : undefined}
      PopperComponent={PopperComponent}
      filterOptions={fetchOnChange ? (options) => options : undefined}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={renderOption}
      popupIcon={<KeyboardArrowDownIcon sx={{ color: "selectIcon" }} />}
      renderInput={externalRenderInput ?? defaultRenderInput}
      data-testing={dataTesting}
    />
  );
}
